import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { CMS_CONTENT_MODES } from '../../cms';
import { Attribute } from '../../decorators/attribute.decorator';
import { BrandColors } from '../../global/helpers-marketing';
import type { UIBadgeContentType, UIBadgeLayout } from './ui-badge.type';
import styles from './ui-badge.css';
import {
    BadgeContentTypes,
    BadgeLayouts,
    validateBadgeColor,
    validateBadgeContentType,
    validateBadgeLayout,
} from './ui-badge.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIBadge
 * @element ui-badge
 * @classdesc Represents a class for <code>ui-badge</code> element.
 * Small badge in the corner for advertising purposes.
 * @property {UIBadgeLayout} [layout] {@attr layout} Shape of the badge.
 * @property {UIBadgeContentType} [contentType] {@attr content-type} A layout can have different types for different usages.
 * @property {UIBrandColor | "yellow"} [color] {@attr color} Color of the badge.
 * @property {number} [count] {@attr count} A numeric value rendered as the badge content. Replaces any content provided via the slot. Should be used with <code>content-type="count"</code>.
 * @property {number} [maxDigits] {@attr max-digits} Opt-in truncation limit for <code>count</code>, given as the maximum amount of digits to display. A count that does not fit is displayed as the largest fitting number followed by a plus sign, for example <code>99+</code> for two digits. Must be a positive integer. Only relevant with <code>count</code> set.
 * @slot
 * @example
 * <ui-badge>Badge</ui-badge>
 * <ui-badge layout="pin" color="orange">1</ui-badge>
 * <ui-badge color="orange" layout="pin"><ui-icon glyph="redesign-heart"></ui-icon></ui-badge>
 * <ui-badge color="yellow" layout="segment">Gold customer</ui-badge>
 * <ui-badge color="lilac" layout="segment-small">Gold customer</ui-badge>
 * <ui-badge layout="pin" content-type="count" count="100" max-digits="2"></ui-badge>
 */
@Component({
    tag: 'ui-badge',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UIBadge extends UIElement {
    /**
     * @type {UIBadgeLayouts}
     */
    static get Layout() {
        return BadgeLayouts;
    }

    /**
     * @type {UIBadgeContentTypes}
     */
    static get ContentType() {
        return BadgeContentTypes;
    }
    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateColor = (component: UIBadge, value: string) => {
        return validateBadgeColor(component, value);
    };

    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateLayout = (component: UIBadge, value: string) => {
        return validateBadgeLayout(component, value);
    };

    /**
     * @param {UIBadge} component
     * @param {string} value
     * @private
     */
    static validateContentType = (component: UIBadge, value: string) => {
        return validateBadgeContentType(component, value);
    };

    @Attribute({ type: String, validate: validateBadgeColor })
    color: BrandColors = BrandColors.Turquoise;

    @Attribute({ type: String, validate: validateBadgeLayout })
    layout: UIBadgeLayout = UIBadge.Layout.Default;

    @Attribute({ type: String, validate: validateBadgeContentType })
    contentType: UIBadgeContentType = UIBadge.ContentType.Tag;

    @Attribute({ type: Number, default: null })
    count: number | null = null;

    @Attribute({ type: Number, default: null })
    maxDigits: number | null = null;

    static get observedAttributes(): Array<string> {
        return ['count', 'max-digits'];
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(name: string): void {
        if (!this.rendered) {
            return;
        }

        switch (name) {
            case 'count':
            case 'max-digits':
                this.updateCount();
                break;
            default:
                break;
        }
    }

    /**
     * Only relevant for Pin layout.
     * Calculates if the badge should have fixed width.
     * Used to style the badge as a circle when it has a short text/number or an icon.
     *
     * Fixed width is applied when:
     * - there is only one text node with max 1 character
     * - there are multiple text nodes but all of them have max 1 character
     * - there are multiple text nodes but only one of them is an element node and all text nodes have max 1 character
     * @private
     * @returns {boolean}
     */
    private hasFixedWidth(): boolean {
        if (this.layout !== UIBadge.Layout.Pin) {
            return false;
        }

        let hasElementNode = false;
        let hasLongTextNode = false;
        let hasShortTextNode = false;

        Array.from(this.childNodes).forEach(
            (node: ChildNode): void | undefined => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    hasElementNode = true;
                    return;
                }

                if (node.nodeType === Node.TEXT_NODE) {
                    const nodeTextValue = node.textContent?.trim();

                    if (nodeTextValue?.length === 0) {
                        return;
                    }

                    if (
                        nodeTextValue?.length !== undefined &&
                        nodeTextValue.length > 1
                    ) {
                        hasLongTextNode = true;
                    } else {
                        hasShortTextNode = true;
                    }
                }
            }
        );
        return !hasLongTextNode && (!hasElementNode || !hasShortTextNode);
    }

    /**
     * Formats the count for display, truncating it when it does not fit into the allowed amount of digits.
     * @returns {string | null} Null when there is no numeric count to display.
     */
    private formatCount(): string | null {
        const { count, maxDigits } = this;

        if (count === null || !Number.isFinite(count)) {
            return null;
        }

        if (
            maxDigits !== null &&
            Number.isInteger(maxDigits) &&
            maxDigits > 0
        ) {
            const maxCount = 10 ** maxDigits - 1;

            if (count > maxCount) {
                return `${maxCount}+`;
            }
        }

        return String(count);
    }

    private updateCount(): void {
        const formattedCount = this.formatCount();

        if (formattedCount !== null && this.textContent !== formattedCount) {
            this.textContent = formattedCount;
        }

        this.updateFixedWidth();
    }

    private updateFixedWidth(): void {
        this.classList.toggle('ui-badge__fixed-width', this.hasFixedWidth());
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.updateCount();
    }
}
